import { SearchOutlined } from "@ant-design/icons";
import { Checkbox } from "@shared/ui/Checkbox";
import { Flex, Input, Spin } from "antd";
import { ReactNode, useEffect, useMemo, useState } from "react";
import { ColumnFilterWrapper } from "./ColumnFilterWrapper";
import styles from "./searchMultiSelectFilter.module.scss";

export type SearchMultiSelectOption = {
  label: string;
  value: string;
};

type SearchMultiSelectFilterProps = {
  title?: ReactNode;
  confirm: () => void;
  close: () => void;
  options: SearchMultiSelectOption[];
  selectedValues: string[];
  onApply: (values: string[]) => void;
  loading?: boolean;
  searchPlaceholder?: string;
  extra?: ReactNode;
  before?: ReactNode;
  filterCount?: number;
  sortActive?: boolean;
  sortDirection?: "asc" | "desc";
  onSortChange?: (dir?: "asc" | "desc") => void;
  onReset?: () => void;
};

export function SearchMultiSelectFilter({
  title,
  confirm,
  close,
  options,
  selectedValues,
  onApply,
  loading = false,
  searchPlaceholder = "Search",
  extra,
  before,
  filterCount,
  sortActive,
  sortDirection,
  onSortChange,
  onReset,
}: SearchMultiSelectFilterProps) {
  const [tempSelected, setTempSelected] = useState<string[]>(selectedValues);

  const cleanPlaceholder = typeof searchPlaceholder === "string"
    ? searchPlaceholder.replace(/^(Select|Search)\s+/i, "")
    : "";
  const displayTitle =
    title ||
    (cleanPlaceholder && cleanPlaceholder !== "Search"
      ? cleanPlaceholder.charAt(0).toUpperCase() + cleanPlaceholder.slice(1)
      : undefined);

  // Sync tempSelected when selectedValues change externally
  useEffect(() => {
    setTempSelected(selectedValues);
  }, [JSON.stringify(selectedValues)]);
  const [searchQuery, setSearchQuery] = useState("");

  const filteredOptions = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return options;
    return options.filter((option) =>
      option.label.toLowerCase().includes(query)
    );
  }, [options, searchQuery]);

  const filteredValues = filteredOptions.map((option) => option.value);
  const allFilteredSelected =
    filteredValues.length > 0 &&
    filteredValues.every((value) => tempSelected.includes(value));

  const handleSelectAll = () => {
    setTempSelected((prev) => {
      const merged = new Set([...prev, ...filteredValues]);
      return Array.from(merged);
    });
  };

  const handleClearAll = () => {
    setTempSelected((prev) =>
      prev.filter((value) => !filteredValues.includes(value))
    );
  };

  const toggleOption = (value: string, checked: boolean) => {
    setTempSelected((prev) =>
      checked ? [...prev, value] : prev.filter((item) => item !== value)
    );
  };

  const handleApply = () => {
    onApply(tempSelected);
    setSearchQuery("");
    confirm();
  };

  const handleResetInternal = () => {
    setTempSelected([]);
    onApply([]);
    setSearchQuery("");
    if (onReset) onReset();
    confirm();
  };

  return (
    <ColumnFilterWrapper
      title={displayTitle}
      filterCount={filterCount ?? tempSelected.length}
      isFiltered={tempSelected.length > 0}
      sortActive={sortActive}
      sortDirection={sortDirection}
      onSortChange={onSortChange}
      confirm={confirm}
      close={close}
      onApply={handleApply}
      onReset={handleResetInternal}
    >
      {before}
      <Flex justify="space-between" align="center" className={styles.toolbar}>
        <Flex gap={4} align="center">
          <button
            type="button"
            className={styles.linkButton}
            onClick={handleSelectAll}
            disabled={filteredOptions.length === 0 || allFilteredSelected}
          >
            Select all {filteredOptions.length}
          </button>
          <span className={styles.toolbarDivider}>-</span>
          <button
            type="button"
            className={styles.linkButton}
            onClick={handleClearAll}
            disabled={filteredOptions.length === 0}
          >
            Clear
          </button>
        </Flex>
        <span className={styles.displayCount}>
          Displaying {filteredOptions.length}
        </span>
      </Flex>

      <Input
        allowClear
        placeholder={searchPlaceholder}
        value={searchQuery}
        onChange={(event) => setSearchQuery(event.target.value)}
        suffix={<SearchOutlined className={styles.searchIcon} />}
        className={styles.searchInput}
      />

      <div className={styles.optionsList}>
        {loading ? (
          <Flex justify="center" align="center" className={styles.loading}>
            <Spin size="small" />
          </Flex>
        ) : filteredOptions.length > 0 ? (
          filteredOptions.map((option) => (
            <label key={option.value} className={styles.optionRow}>
              <Checkbox
                checked={tempSelected.includes(option.value)}
                onChange={(event) =>
                  toggleOption(option.value, event.target.checked)
                }
              />
              <span className={styles.optionLabel}>{option.label}</span>
            </label>
          ))
        ) : (
          <div className={styles.emptyState}>No results found</div>
        )}
      </div>

      {extra}
    </ColumnFilterWrapper>
  );
}
